import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { LeadSearchFilters } from '../types/leadSearch';
import type { UserReference, InsuranceCompanyReference, LeadStatus, DamageCategory } from '../types';
import { getStates, getDamageCategories } from '../api/leads-api';

interface LeadSearchFormProps {
  filters: LeadSearchFilters;
  onFiltersChange: (filters: LeadSearchFilters) => void;
  onSearch: () => void;
  onClear: () => void;
  users: UserReference[];
  insuranceCompanies: InsuranceCompanyReference[];
}

const statuses: LeadStatus[] = ['Unassigned', 'Assigned', 'Sold', 'NoGo', 'Closed', 'Collections'];

export function LeadSearchForm({
  filters,
  onFiltersChange,
  onSearch,
  onClear,
  users,
  insuranceCompanies,
}: LeadSearchFormProps) {
  const [customerInfoOpen, setCustomerInfoOpen] = useState(true);
  const [propertyAddressOpen, setPropertyAddressOpen] = useState(false);
  const [billingAddressOpen, setBillingAddressOpen] = useState(false);
  const [jobInfoOpen, setJobInfoOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);

  const states = getStates();
  const damageCategories = getDamageCategories();

  const handleInputChange = (field: keyof LeadSearchFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    });
  };

  const handleSelectChange = (
    field: keyof LeadSearchFilters,
    value: string | number | undefined
  ) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    });
  };

  return (
    <div className="bg-white rounded-[20px] p-6 space-y-4">
      {/* Customer Info Section */}
      <Collapsible open={customerInfoOpen} onOpenChange={setCustomerInfoOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-semibold text-lg">
          {customerInfoOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Customer Info
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={filters.firstName || ''}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="Enter first name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={filters.lastName || ''}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Enter last name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="companyName">Company</Label>
              <Input
                id="companyName"
                value={filters.companyName || ''}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                placeholder="Enter company name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={filters.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="Enter phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={filters.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter email address"
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Property Address Section */}
      <Collapsible open={propertyAddressOpen} onOpenChange={setPropertyAddressOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-semibold text-lg">
          {propertyAddressOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Property Address
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2 lg:col-span-2">
              <Label htmlFor="propertyStreet">Street</Label>
              <Input
                id="propertyStreet"
                value={filters.propertyStreet || ''}
                onChange={(e) => handleInputChange('propertyStreet', e.target.value)}
                placeholder="Enter street address"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="propertyCity">City</Label>
              <Input
                id="propertyCity"
                value={filters.propertyCity || ''}
                onChange={(e) => handleInputChange('propertyCity', e.target.value)}
                placeholder="Enter city"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="propertyState">State</Label>
              <Select
                id="propertyState"
                value={filters.propertyState || ''}
                onChange={(e) => handleSelectChange('propertyState', e.target.value)}
              >
                <option value="">Select state</option>
                {states.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="propertyZip">Zip</Label>
              <Input
                id="propertyZip"
                value={filters.propertyZip || ''}
                onChange={(e) => handleInputChange('propertyZip', e.target.value)}
                placeholder="Enter zip code"
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Billing Address Section */}
      <Collapsible open={billingAddressOpen} onOpenChange={setBillingAddressOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-semibold text-lg">
          {billingAddressOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Billing Address
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2 lg:col-span-2">
              <Label htmlFor="billingStreet">Street</Label>
              <Input
                id="billingStreet"
                value={filters.billingStreet || ''}
                onChange={(e) => handleInputChange('billingStreet', e.target.value)}
                placeholder="Enter street address"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="billingCity">City</Label>
              <Input
                id="billingCity"
                value={filters.billingCity || ''}
                onChange={(e) => handleInputChange('billingCity', e.target.value)}
                placeholder="Enter city"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="billingState">State</Label>
              <Select
                id="billingState"
                value={filters.billingState || ''}
                onChange={(e) => handleSelectChange('billingState', e.target.value)}
              >
                <option value="">Select state</option>
                {states.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="billingZip">Zip</Label>
              <Input
                id="billingZip"
                value={filters.billingZip || ''}
                onChange={(e) => handleInputChange('billingZip', e.target.value)}
                placeholder="Enter zip code"
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Job Info Section */}
      <Collapsible open={jobInfoOpen} onOpenChange={setJobInfoOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-semibold text-lg">
          {jobInfoOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Job Info
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="jobNumber">Job Number</Label>
              <Input
                id="jobNumber"
                value={filters.jobNumber || ''}
                onChange={(e) => handleInputChange('jobNumber', e.target.value)}
                placeholder="Enter job number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                id="status"
                value={filters.status || ''}
                onChange={(e) => handleSelectChange('status', e.target.value as LeadStatus)}
              >
                <option value="">All statuses</option>
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="damageType">Damage Type</Label>
              <Select
                id="damageType"
                value={filters.damageType || ''}
                onChange={(e) => handleSelectChange('damageType', e.target.value as DamageCategory)}
              >
                <option value="">All types</option>
                {damageCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Dates Section */}
      <Collapsible open={datesOpen} onOpenChange={setDatesOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-semibold text-lg">
          {datesOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Dates
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="leadDateFrom">Lead Date From</Label>
              <Input
                id="leadDateFrom"
                type="date"
                value={filters.leadDateFrom || ''}
                onChange={(e) => handleInputChange('leadDateFrom', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="leadDateTo">Lead Date To</Label>
              <Input
                id="leadDateTo"
                type="date"
                value={filters.leadDateTo || ''}
                onChange={(e) => handleInputChange('leadDateTo', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="openedDateFrom">Opened Date From</Label>
              <Input
                id="openedDateFrom"
                type="date"
                value={filters.openedDateFrom || ''}
                onChange={(e) => handleInputChange('openedDateFrom', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="openedDateTo">Opened Date To</Label>
              <Input
                id="openedDateTo"
                type="date"
                value={filters.openedDateTo || ''}
                onChange={(e) => handleInputChange('openedDateTo', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="closedDateFrom">Closed Date From</Label>
              <Input
                id="closedDateFrom"
                type="date"
                value={filters.closedDateFrom || ''}
                onChange={(e) => handleInputChange('closedDateFrom', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="closedDateTo">Closed Date To</Label>
              <Input
                id="closedDateTo"
                type="date"
                value={filters.closedDateTo || ''}
                onChange={(e) => handleInputChange('closedDateTo', e.target.value)}
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Assignment Section */}
      <Collapsible open={assignmentOpen} onOpenChange={setAssignmentOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-semibold text-lg">
          {assignmentOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Assignment
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="assignedToUserId">Estimator</Label>
              <Select
                id="assignedToUserId"
                value={filters.assignedToUserId?.toString() || ''}
                onChange={(e) =>
                  handleSelectChange(
                    'assignedToUserId',
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
              >
                <option value="">All estimators</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="insuranceCompanyId">Insurance Company</Label>
              <Select
                id="insuranceCompanyId"
                value={filters.insuranceCompanyId?.toString() || ''}
                onChange={(e) =>
                  handleSelectChange(
                    'insuranceCompanyId',
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
              >
                <option value="">All companies</option>
                {insuranceCompanies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t">
        <Button onClick={onSearch} className="min-w-[120px]">
          Search
        </Button>
        <Button onClick={onClear} variant="outline" className="min-w-[120px]">
          Clear
        </Button>
      </div>
    </div>
  );
}
